import { Text, Tooltip } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Select } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { OperationChain } from "@tfg/types/schemas/enum"
import { GetEcobalyseMaterialTypesResponseDto } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/get-ecobalyse-material-types.response.dto"

import { queryClient } from "~/backend/queryClient"
import { useDeleteMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useDeleteMaterialTypeMutation"
import { useUpdateMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useUpdateMaterialTypeMutation"

import { EditableCellWithTextInput } from "../../Translations/columns"
import { useUpdateTranslationsMutation } from "../../Translations/hooks"

const columnHelper = createColumnHelper<AdminResponses.MaterialTypes>()
export const useColumns = (
  materialOpChains: OperationChain[],
  ecobalyseMaterialTypes: GetEcobalyseMaterialTypesResponseDto
) => {
  const { mutate: updateTranslation } = useUpdateTranslationsMutation({
    onSuccess: () => queryClient.invalidateQueries(["materialtypes"]),
    onError: () =>
      notifications.show({
        title: "Error",
        message: "Failed to update translation",
        color: "red",
      }),
  })
  const { mutate: updateMaterialType } = useUpdateMaterialTypeMutation()
  const { mutate: deleteMaterialType } = useDeleteMaterialTypeMutation()

  return useMemo<ColumnDef<AdminResponses.MaterialTypes, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("key", {
        header: "Key",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("category", {
        header: "Category",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor(
        (materialType) => {
          return materialType
        },
        {
          header: "Ecobalyse Id",
          enableSorting: false,
          cell: ({ getValue }) => {
            const materialType = getValue()

            return (
              <Select
                searchable
                clearable
                comboboxProps={{ width: "fit-content" }}
                defaultValue={materialType.ecobalyseId}
                data={ecobalyseMaterialTypes?.map((ecobalyseMaterialType) => ({
                  value: ecobalyseMaterialType.id,
                  label: ecobalyseMaterialType.name,
                }))}
                onChange={(newValue) => {
                  updateMaterialType({
                    materialId: materialType.id,
                    materialType: {
                      key: materialType.key,
                      category: materialType.category,
                      ecobalyseId: newValue,
                      operationChainId: materialType.materialOpChain.id,
                    },
                  })
                }}
              />
            )
          },
        }
      ),
      columnHelper.accessor((materialType) => materialType, {
        header: "materialOpChain",
        enableSorting: false,
        cell: ({ getValue }) => {
          const materialType = getValue()
          return (
            <Tooltip
              multiline
              maw={300}
              label="Changing this will have an impact on existing information requests, please contact tech team to change this."
            >
              <Select
                defaultValue={materialType.materialOpChain.id}
                data={Object.values(materialOpChains).map((chain) => ({
                  label: chain.name,
                  value: chain.id,
                }))}
                disabled={true}
                onChange={(newValue) => {
                  if (newValue === null) return
                  updateMaterialType({
                    materialId: materialType.id,
                    materialType: {
                      key: materialType.key,
                      category: materialType.category,
                      ecobalyseId: materialType.ecobalyseId,
                      operationChainId: newValue,
                    },
                  })
                }}
              />
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor("EN", {
        header: "EN",
        enableSorting: false,
        cell: ({ row: { original: materialType }, getValue }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: materialType.typeTextContentId,
                translatedLanguage: "EN",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor("FR", {
        header: "FR",
        enableSorting: false,
        cell: ({ row: { original: materialType }, getValue }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: materialType.typeTextContentId,
                translatedLanguage: "FR",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "delete",
        enableSorting: false,
        cell: ({ row }) => (
          <ActionIcon
            type="delete"
            onClick={() => deleteMaterialType(row.original.id)}
          />
        ),
      }),
    ],
    [
      updateTranslation,
      updateMaterialType,
      deleteMaterialType,
      ecobalyseMaterialTypes,
      materialOpChains,
    ]
  )
}
